import {
  CardBlock,
  DocumentBlock,
  DocumentField,
  DocumentSection,
  Inline,
  ResolvedDocument,
  choiceId,
  isField,
  isHidden
} from "./types";

// What a slot shows now; null for a blank. Undefined keeps the value the document came with.
export type SlotValueOf = (slot: string) => string | null | undefined;

const fill = <F extends DocumentField>(field: F, valueOf: SlotValueOf): F => {
  const value = valueOf(field.slot);
  return value === undefined || value === field.value
    ? field
    : { ...field, value };
};

const fillText = (parts: Inline[], valueOf: SlotValueOf) =>
  parts.map((part) => (isField(part) ? fill(part, valueOf) : part));

const fillCard = (block: CardBlock, valueOf: SlotValueOf): CardBlock => ({
  ...block,
  band: block.band.map((field) => fill(field, valueOf)),
  sections: block.sections.map((section) => ({
    columns: section.columns.map((column) => ({
      ...column,
      fields: column.fields.map((field) => fill(field, valueOf))
    }))
  }))
});

const fillBlock = (
  block: DocumentBlock,
  valueOf: SlotValueOf,
  included: Record<string, boolean>
): DocumentBlock => {
  const filled = fillFields(block, valueOf);
  const id = choiceId(filled);
  // The fixed check only narrows the type: a fixed block has no choice id.
  return filled.type !== "fixed" && id !== undefined && id in included
    ? { ...filled, included: included[id] }
    : filled;
};

const fillFields = (
  block: DocumentBlock,
  valueOf: SlotValueOf
): DocumentBlock => {
  switch (block.type) {
    case "heading":
    case "paragraph":
    case "script":
      return { ...block, text: fillText(block.text, valueOf) };
    case "bullets":
      return {
        ...block,
        items: block.items.map((item) => fillText(item, valueOf))
      };
    case "fixed":
      return {
        ...block,
        items: block.items.map((item) => fill(item, valueOf))
      };
    case "table":
      return block.total
        ? { ...block, total: fill(block.total, valueOf) }
        : block;
    case "card":
      return fillCard(block, valueOf);
    default:
      return block;
  }
};

const fillSection = (
  section: DocumentSection,
  valueOf: SlotValueOf,
  included: Record<string, boolean>
): DocumentSection => ({
  ...section,
  ...(section.title ? { title: fillText(section.title, valueOf) } : {}),
  blocks: section.blocks.map((block) => fillBlock(block, valueOf, included))
});

// The document as the rep sees it now: their values in every field, and their
// choice for each optional block they changed.
export const withValues = (
  resolvedDocument: ResolvedDocument,
  valueOf: SlotValueOf,
  included: Record<string, boolean> = {}
): ResolvedDocument => ({
  ...resolvedDocument,
  page: {
    ...resolvedDocument.page,
    ...(resolvedDocument.page.footer
      ? {
          footer: {
            ...resolvedDocument.page.footer,
            name: fill(resolvedDocument.page.footer.name, valueOf)
          }
        }
      : {})
  },
  sections: resolvedDocument.sections.map((section) =>
    fillSection(section, valueOf, included)
  )
});

const fieldsOfBlock = (block: DocumentBlock): DocumentField[] => {
  switch (block.type) {
    case "heading":
    case "paragraph":
    case "script":
      return block.text.filter(isField);
    case "bullets":
      return block.items.flatMap((item) => item.filter(isField));
    // Top of the page first, then left to right.
    case "fixed":
      return [...block.items].sort(
        (a, b) => b.baseline - a.baseline || a.x - b.x
      );
    case "table":
      return block.total ? [block.total] : [];
    // Row by row across the columns, as the card reads.
    case "card":
      return [
        ...block.band,
        ...block.sections.flatMap((section) => {
          const rows = Math.max(
            ...section.columns.map((column) => column.fields.length)
          );
          return Array.from({ length: rows }, (_, row) =>
            section.columns.flatMap((column) =>
              column.fields[row] ? [column.fields[row]] : []
            )
          ).flat();
        })
      ];
    default:
      return [];
  }
};

const slotsOf = (
  resolvedDocument: ResolvedDocument,
  blockShown: (block: DocumentBlock) => boolean
): string[] => [
  ...new Set(
    [
      ...resolvedDocument.sections.flatMap((section) => [
        ...(section.title ?? []).filter(isField),
        ...section.blocks.filter(blockShown).flatMap(fieldsOfBlock)
      ]),
      ...(resolvedDocument.page.footer
        ? [resolvedDocument.page.footer.name]
        : [])
    ].map((field) => field.slot)
  )
];

// Each slot once, where the document first shows it; the footer, on every page, comes last.
export const slotsInReadingOrder = (
  resolvedDocument: ResolvedDocument
): string[] => slotsOf(resolvedDocument, () => true);

// The slots the document shows: none from an optional block left out.
export const slotsShown = (resolvedDocument: ResolvedDocument): Set<string> =>
  new Set(slotsOf(resolvedDocument, (block) => !isHidden(block)));

// Each optional block's choice as the document holds it, by block id.
export const includedOf = (
  resolvedDocument: ResolvedDocument
): Record<string, boolean> =>
  Object.fromEntries(
    resolvedDocument.sections.flatMap((section) =>
      section.blocks.flatMap((block) => {
        const id = choiceId(block);
        return id === undefined ? [] : [[id, !isHidden(block)]];
      })
    )
  );
